import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { bakeFrames } from '../art/bake';
import { HORSE_GALLOP, HORSE_IDLE, HORSE_TUCK } from '../art/sprites/horse';
import { bindKeys } from '../input/InputController';
import { VIRTUAL_H, VIRTUAL_W } from '../render/StageScaler';
import { MINIGAMES } from '../sim/minigames';
import {
  DesensitizeSession,
  type DesensitizeObject,
} from '../sim/minigames/desensitize';
import { FlyingStartSession } from '../sim/minigames/flying-start';
import { FurlongsSession } from '../sim/minigames/furlongs';
import { GearsSession, type GearCue } from '../sim/minigames/gears';
import { HillsSession } from '../sim/minigames/hills';
import { PolesSession } from '../sim/minigames/poles';
import { TempoSession } from '../sim/minigames/tempo';
import { TrustSession } from '../sim/minigames/trust';
import { xpFor, type MinigameId, type MinigameSession } from '../sim/minigames/framework';
import { el } from '../ui/dom';
import {
  InputCollector,
  SessionRunner,
  launchPalette,
  showTrainingResults,
  trainingControls,
  type TrainingControlSpec,
  type TrainingLaunch,
  type TrainingSceneOptions,
} from './minigameCommon';
import type { Scene, SceneContext } from './SceneManager';
import {
  drawSpookObject,
  drawTrainingArrow,
  drawTrustObstacle,
  type TrustObstacleKind,
} from './trainingGraphics';

type RotatingMode = Exclude<MinigameId, 'gallop' | 'trail' | 'gates' | 'pen'>;
type CueTone = 'go' | 'good' | 'bad' | 'quiet';
type MeterTone = 'safe' | 'warning' | 'danger' | 'position';

interface HudState {
  cue: string;
  status: string;
  clock: string;
  meter: number;
  meterLabel: string;
  cueTone: CueTone;
  meterTone: MeterTone;
}

const HOLD_MODES: readonly RotatingMode[] = ['furlongs', 'hills', 'flyingStart', 'trust'];
const STEER_MODES: readonly RotatingMode[] = ['poles', 'gears', 'desensitize'];
const HORSE_X = 132;
const GROUND_Y = 218;
const PROMPT_LEAD_TICKS = 42;

const HINTS: Record<RotatingMode, string> = {
  furlongs: 'Work the green interval, release in amber recovery, and keep Heat below red',
  poles: 'Move to the called side before each pole reaches your horse',
  hills: 'Drive uphill, release downhill, and save enough Effort to finish',
  tempo: 'Tap when each pair of hoofprints crosses the gold strike line',
  flyingStart: 'Hold to load the horse, then release—not tap—when the light turns green',
  gears: 'Tap the direction shown as its arrow reaches the gold strike line',
  desensitize: 'Counter away from real hazards; ignore dim harmless decoys',
  trust: 'Approach, release when she hesitates, then hold again to cross',
};

const OBJECT_LABEL: Record<DesensitizeObject, string> = {
  tarp: 'TARP',
  goose: 'GOOSE',
  umbrella: 'UMBRELLA',
  bucket: 'BUCKET',
};

const TRUST_OBSTACLES: readonly TrustObstacleKind[] = ['puddle', 'tarp', 'bridge', 'barrels'];

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function arrowLabel(cue: GearCue): string {
  return cue === 'left' ? '←' : cue === 'right' ? '→' : '↑';
}

function drawCloud(graphics: Graphics, x: number, y: number, scale = 1): void {
  graphics
    .ellipse(x, y + 5 * scale, 15 * scale, 7 * scale).fill(0xd9e0e4)
    .ellipse(x - 10 * scale, y + 4 * scale, 9 * scale, 6 * scale).fill(0xf1f3ee)
    .ellipse(x + 8 * scale, y, 11 * scale, 9 * scale).fill(0xf1f3ee)
    .rect(x - 17 * scale, y + 4 * scale, 35 * scale, 7 * scale).fill(0xf1f3ee);
}

function drawFence(graphics: Graphics, y: number, color = 0x775638): void {
  graphics.rect(0, y, VIRTUAL_W, 4).fill(0x35271c);
  graphics.rect(0, y + 1, VIRTUAL_W, 2).fill(color);
  for (let x = 8; x < VIRTUAL_W; x += 34) {
    graphics.rect(x, y - 12, 5, 24).fill(0x35271c).rect(x + 1, y - 10, 3, 20).fill(color);
  }
}

/** Eight genuinely different playfields backed by eight deterministic
 * simulations. The old implementation put every mode on the same three
 * color bands and left most of the simulated state invisible. */
export class RotatingTrainingScene implements Scene {
  private ctx!: SceneContext;
  private session!: MinigameSession;
  private runner!: SessionRunner;
  private collector!: InputCollector;
  private container!: Container;
  private staticArt!: Graphics;
  private world!: Graphics;
  private horse!: Sprite;
  private gallopFrames!: Texture[];
  private idleFrames!: Texture[];
  private tuckTexture!: Texture;
  private hud!: HTMLElement;
  private cue!: HTMLElement;
  private status!: HTMLElement;
  private time!: HTMLElement;
  private meterFill!: HTMLElement;
  private meterLabel!: HTMLElement;
  private flash!: HTMLElement;
  private unbind: (() => void) | null = null;
  private disposeResults: (() => void) | null = null;
  private resultsShown = false;
  private travel = 0;
  private lastNowMs = 0;
  private lastA = 0;
  private lastB = 0;
  private lastPhase = '';
  private burstVisualTicks = 0;

  constructor(
    private readonly mode: RotatingMode,
    private readonly launch: TrainingLaunch,
    private readonly options: TrainingSceneOptions,
  ) {}

  enter(ctx: SceneContext): void {
    this.ctx = ctx;
    this.session = MINIGAMES[this.mode].create(this.launch.tier, this.launch.seed, this.launch.mods);
    this.runner = new SessionRunner();
    this.collector = new InputCollector({
      holdCodes: HOLD_MODES.includes(this.mode) ? ['ArrowUp', 'KeyW'] : undefined,
      pressCodes: this.mode === 'tempo' || this.mode === 'gears' ? ['ArrowUp', 'KeyW'] : undefined,
      steer: STEER_MODES.includes(this.mode),
    });

    this.container = new Container();
    this.staticArt = new Graphics();
    this.world = new Graphics();
    this.gallopFrames = bakeFrames(HORSE_GALLOP, launchPalette(this.launch));
    this.idleFrames = bakeFrames(HORSE_IDLE, launchPalette(this.launch));
    this.tuckTexture = bakeFrames(HORSE_TUCK, launchPalette(this.launch))[0]!;
    this.horse = new Sprite(this.gallopFrames[0]!);
    this.container.addChild(this.staticArt, this.world, this.horse);
    this.drawBackdrop();
    ctx.scaler.root.addChild(this.container);

    this.time = el('span', { class: 'hud-chip' });
    this.status = el('span', { class: 'hud-chip' });
    this.cue = el('div', { class: 'training-cue', 'aria-live': 'polite' });
    this.meterLabel = el('span', { class: 'training-meter-label' });
    this.meterFill = el('div', { class: 'training-meter-fill' });
    this.flash = el('div', { class: 'race-flash training-flash' });
    this.hud = el(
      'div',
      { class: `drill-hud with-training-controls mode-${this.mode}` },
      el(
        'div',
        { class: 'ranch-topbar' },
        el('span', { class: 'hud-chip horse' }, MINIGAMES[this.mode].name),
        this.time,
        this.status,
      ),
      this.cue,
      this.flash,
      el(
        'div',
        { class: 'training-meter-dock' },
        this.meterLabel,
        el('div', { class: 'training-meter' }, this.meterFill),
      ),
      trainingControls(this.collector, this.controlSpecs()),
      el('div', { class: 'ranch-hints' }, `${HINTS[this.mode]} · Esc ends early`),
    );
    ctx.uiRoot.append(this.hud);
    this.unbind = bindKeys([{ code: 'Escape', handler: () => this.endEarly() }]);
  }

  frame(nowMs: number): void {
    const steps = this.runner.advance(nowMs, this.session, this.collector);
    this.travel += steps;
    this.burstVisualTicks = Math.max(0, this.burstVisualTicks - steps);
    this.captureFeedback();
    this.draw(nowMs);
    if (this.session.finished && !this.resultsShown) this.showResults();
  }

  private controlSpecs(): readonly TrainingControlSpec[] {
    switch (this.mode) {
      case 'furlongs': return [{ action: 'primary', mode: 'hold', label: 'PUSH ↑' }];
      case 'hills': return [{ action: 'primary', mode: 'hold', label: 'DRIVE ↑' }];
      case 'tempo': return [{ action: 'primary', mode: 'press', label: 'TAP ↑' }];
      case 'flyingStart': return [{ action: 'primary', mode: 'hold', label: 'LOAD ↑' }];
      case 'trust': return [{ action: 'primary', mode: 'hold', label: 'REIN ↑' }];
      case 'poles':
        return [
          { action: 'left', mode: 'hold', label: '← LEFT' },
          { action: 'right', mode: 'hold', label: 'RIGHT →' },
        ];
      case 'gears':
        return [
          { action: 'left', mode: 'press', label: '←' },
          { action: 'primary', mode: 'press', label: '↑' },
          { action: 'right', mode: 'press', label: '→' },
        ];
      case 'desensitize':
        return [
          { action: 'left', mode: 'press', label: '← COUNTER' },
          { action: 'right', mode: 'press', label: 'COUNTER →' },
        ];
    }
  }

  private drawBackdrop(): void {
    const g = this.staticArt;
    g.clear().rect(0, 0, VIRTUAL_W, VIRTUAL_H).fill(0x87a8c8);
    drawCloud(g, 70, 35, 0.8);
    drawCloud(g, 362, 47, 0.6);

    if (this.mode === 'furlongs' || this.mode === 'flyingStart' || this.mode === 'gears') {
      g
        .rect(0, 88, VIRTUAL_W, 70).fill(0x5d7a44)
        .rect(0, 158, VIRTUAL_W, 112).fill(0x8a6a45)
        .rect(0, 166, VIRTUAL_W, 3).fill(0xc8ad72)
        .rect(0, 216, VIRTUAL_W, 3).fill(0x60462f);
      for (let x = 12; x < VIRTUAL_W; x += 37) {
        g.rect(x, 127 + ((x / 37) % 2) * 6, 3, 9).fill(0x4f693a);
      }
      drawFence(g, 150, 0x977047);
      return;
    }

    if (this.mode === 'hills' || this.mode === 'tempo') {
      g
        .poly([0, 112, 78, 62, 146, 110, 226, 54, 330, 113, 410, 66, 480, 105, 480, 168, 0, 168]).fill(0x506b50)
        .poly([0, 129, 90, 88, 176, 132, 274, 82, 385, 125, 480, 91, 480, 179, 0, 179]).fill(0x5f7d4f)
        .rect(0, 150, VIRTUAL_W, 120).fill(0x66844c);
      for (let x = 18; x < VIRTUAL_W; x += 52) {
        g.poly([x, 153, x + 8, 127, x + 16, 153]).fill(0x35513b).rect(x + 7, 151, 3, 13).fill(0x4a3828);
      }
      return;
    }

    g
      .rect(0, 80, VIRTUAL_W, VIRTUAL_H - 80).fill(0x5d7a44)
      .rect(28, 135, VIRTUAL_W - 56, 116).fill(0x8a6a45);
    drawFence(g, 128, 0x98734a);
    drawFence(g, 242, 0x98734a);
  }

  private draw(nowMs: number): void {
    this.lastNowMs = nowMs;
    this.world.clear();
    switch (this.mode) {
      case 'furlongs': this.drawFurlongs(); break;
      case 'poles': this.drawPoles(); break;
      case 'hills': this.drawHills(); break;
      case 'tempo': this.drawTempo(); break;
      case 'flyingStart': this.drawFlyingStart(); break;
      case 'gears': this.drawGears(); break;
      case 'desensitize': this.drawDesensitize(); break;
      case 'trust': this.drawTrust(); break;
    }

    const state = this.readState();
    this.cue.textContent = state.cue;
    this.cue.className = `training-cue ${state.cueTone}`;
    this.status.textContent = state.status;
    this.time.textContent = state.clock;
    this.meterLabel.textContent = state.meterLabel;
    this.meterFill.className = `training-meter-fill ${state.meterTone}`;
    this.meterFill.style.width = `${Math.round(clamp01(state.meter) * 100)}%`;
  }

  private drawFurlongs(): void {
    const s = this.session as FurlongsSession;
    const g = this.world;
    const pxPerTick = 4.2;
    for (const segment of s.segments) {
      if (segment.endTick < s.tick - 5 || segment.startTick > s.tick + 120) continue;
      const x = HORSE_X + (segment.startTick - s.tick) * pxPerTick;
      const endX = HORSE_X + (segment.endTick - s.tick) * pxPerTick;
      g
        .rect(x, 170, endX - x, 45)
        .fill({ color: segment.work ? 0x4b8b51 : 0xc08b40, alpha: 0.28 })
        .rect(x - 2, 163, 4, 57).fill(segment.work ? 0x86cb82 : 0xe2b85f)
        .rect(x + 2, 163, 14, 7).fill(segment.work ? 0x46794a : 0x9b6a2d);
    }
    const streaks = Math.floor(s.pace * 7);
    for (let index = 0; index < streaks; index += 1) {
      const x = 18 + ((s.tick * (4 + index) + index * 53) % 92);
      const y = 178 + ((index * 11) % 32);
      g.rect(x, y, 10 + index * 2, 2).fill({ color: 0xe5d5a4, alpha: 0.35 + index * 0.06 });
    }
    this.drawHorseShadow(HORSE_X, GROUND_Y);
    this.setHorse(HORSE_X, GROUND_Y, 'gallop', s.tick * (0.05 + s.pace * 0.25), s.heat > 0.82 ? 0xffc3ad : 0xffffff);
  }

  private drawPoles(): void {
    const s = this.session as PolesSession;
    const g = this.world;
    const centerX = 240;
    g
      .rect(126, 135, 3, 99).fill({ color: 0xeadbb9, alpha: 0.45 })
      .rect(351, 135, 3, 99).fill({ color: 0xeadbb9, alpha: 0.45 });
    for (let y = 139; y < 235; y += 18) g.rect(centerX - 1, y, 3, 9).fill({ color: 0xf0e1bf, alpha: 0.35 });

    const first = s.nextTarget() ? s.poles.indexOf(s.nextTarget()!) : s.poles.length;
    for (let index = first; index < Math.min(s.poles.length, first + 5); index += 1) {
      const pole = s.poles[index]!;
      const dt = pole.tick - s.tick;
      const y = 76 + (1 - Math.min(1, Math.max(0, dt / 70))) * 142;
      const x = centerX + pole.side * 78;
      const active = index === first;
      if (active) g.ellipse(x, y + 3, 18, 7).fill({ color: 0xd9a441, alpha: 0.28 });
      g
        .ellipse(x, y + 4, 7, 3).fill(0x3b2a1c)
        .rect(x - 3, y - 23, 6, 27).fill(0xefe8d2)
        .rect(x - 3, y - 17, 6, 6).fill(0xc4574a)
        .rect(x - 3, y - 5, 6, 6).fill(0xc4574a);
      if (active && dt < 28) drawTrainingArrow(g, x, y - 38, pole.side < 0 ? 'left' : 'right', 0xd9a441, 0.7);
    }
    const horseX = centerX + s.p * 96;
    this.drawHorseShadow(horseX, GROUND_Y);
    this.setHorse(horseX, GROUND_Y, 'gallop', s.tick * 0.24);
  }

  private drawHills(): void {
    const s = this.session as HillsSession;
    const g = this.world;
    const climb = s.segmentNow().climb;
    if (climb) {
      g
        .poly([0, 246, 90, 230, 180, 205, 280, 173, 380, 146, 480, 127, 480, 270, 0, 270]).fill(0x6e8b50)
        .poly([0, 242, 90, 226, 180, 201, 280, 169, 380, 142, 480, 123, 480, 133, 380, 152, 280, 179, 180, 211, 90, 236, 0, 252]).fill(0x9a754a);
    } else {
      g
        .poly([0, 138, 90, 157, 180, 182, 280, 214, 380, 239, 480, 250, 480, 270, 0, 270]).fill(0x6e8b50)
        .poly([0, 134, 90, 153, 180, 178, 280, 210, 380, 235, 480, 246, 480, 256, 380, 245, 280, 220, 180, 188, 90, 163, 0, 144]).fill(0x9a754a);
    }
    // The trail is a piecewise ridge, not a single line from the origin.
    // These are its interpolated heights at the horse's fixed screen X.
    const groundY = climb ? 219 : 170;
    for (let index = 0; index < 5; index += 1) {
      const x = ((index * 113 - s.tick * 2.2) % 570 + 570) % 570 - 45;
      const y = climb ? 242 - x * 0.3 : 139 + x * 0.29;
      g.rect(x, y - 8, 3, 10).fill(0x423426).poly([x - 7, y - 7, x + 1, y - 23, x + 9, y - 7]).fill(0x3e633f);
    }
    if (climb) drawTrainingArrow(g, 350, 105, 'up', 0x8fd48f, 0.8);
    this.drawHorseShadow(HORSE_X, groundY);
    this.setHorse(HORSE_X, groundY, 'gallop', s.tick * (climb ? 0.16 : 0.24), s.effort < 0.2 ? 0xffc3ad : 0xffffff);
  }

  private drawTempo(): void {
    const s = this.session as TempoSession;
    const g = this.world;
    g
      .poly([0, 270, 78, 174, 298, 174, 480, 270]).fill(0x80613f)
      .poly([138, 270, 181, 174, 195, 174, 210, 270]).fill({ color: 0xc29b61, alpha: 0.35 })
      .rect(152, 205, 4, 49).fill(0xd9a441)
      .rect(156, 205, 2, 49).fill(0x513b27);
    const start = s.nextBeatIndex();
    for (let index = start; index < Math.min(s.beats.length, start + 6); index += 1) {
      const beat = s.beats[index]!;
      const dt = beat.tick - s.tick;
      if (dt > PROMPT_LEAD_TICKS * 1.6 || dt < -8) continue;
      const x = 156 + Math.max(-10, dt) / (PROMPT_LEAD_TICKS * 1.6) * 305;
      const close = Math.abs(dt) <= s.timingWindowTicks();
      const color = close ? 0xf5df78 : 0xe8e0cc;
      g
        .ellipse(x - 5, 222, 5, 9).fill(0x2b2118)
        .ellipse(x + 6, 238, 5, 9).fill(0x2b2118)
        .ellipse(x - 5, 220, 3, 6).fill(color)
        .ellipse(x + 6, 236, 3, 6).fill(color);
    }
    this.drawHorseShadow(HORSE_X, 194);
    this.setHorse(HORSE_X, 194, 'gallop', s.tick * 0.28, s.breath < 0.3 ? 0xffc3ad : 0xffffff);
  }

  private drawFlyingStart(): void {
    const s = this.session as FlyingStartSession;
    const g = this.world;
    const gateX = 105;
    g
      .rect(gateX - 20, 128, 6, 91).fill(0x3b3f43)
      .rect(gateX + 40, 128, 6, 91).fill(0x3b3f43)
      .rect(gateX - 20, 128, 66, 7).fill(0x737a7e)
      .rect(gateX - 13, 143, 48, 4).fill(0xabb0ad)
      .rect(gateX - 13, 167, 48, 4).fill(0xabb0ad)
      .rect(gateX - 13, 191, 48, 4).fill(0xabb0ad)
      .rect(gateX - 37, 99, 29, 29).fill(0x27231d);
    const live = s.phase === 'release';
    g.ellipse(gateX - 23, 113, 8, 8).fill(live ? 0x7ed77e : 0x5c2922);
    g.ellipse(gateX - 23, 113, 4, 4).fill(live ? 0xd1ffd1 : 0xd65d4c);

    let horseX = gateX + 11;
    let gait: 'idle' | 'gallop' | 'tuck' = s.chargeFraction() > 0.05 ? 'tuck' : 'idle';
    let tint = 0xffffff;
    if (live) {
      horseX += Math.min(1, s.phaseTick / 18) * 250;
      gait = 'gallop';
    } else if (this.burstVisualTicks > 0 && s.lastGrade > 0) {
      horseX += (1 - this.burstVisualTicks / 18) * 260;
      gait = 'gallop';
      tint = s.lastGrade >= 0.9 ? 0xffe4a2 : 0xffffff;
    }
    if (s.phase === 'charge' && s.chargeFraction() > 0) {
      const loadW = Math.round(s.chargeFraction() * 54);
      g.rect(gateX - 15, 119, 58, 5).fill(0x211a14).rect(gateX - 13, 120, loadW, 3).fill(0xd9a441);
    }
    this.drawHorseShadow(horseX, GROUND_Y);
    this.setHorse(horseX, GROUND_Y, gait, s.phaseTick * 0.35, tint);
  }

  private drawGears(): void {
    const s = this.session as GearsSession;
    const g = this.world;
    g
      .rect(148, 112, 4, 75).fill(0xd9a441)
      .rect(154, 112, 2, 75).fill(0x5a422a)
      .rect(150, 146, 310, 4).fill({ color: 0xefe8d2, alpha: 0.28 });
    const first = s.nextPromptIndex();
    for (let index = first; index < Math.min(s.prompts.length, first + 5); index += 1) {
      const prompt = s.prompts[index]!;
      const dt = prompt.tick - s.tick;
      if (dt > PROMPT_LEAD_TICKS || dt < -8) continue;
      const x = 152 + Math.max(-7, dt) / PROMPT_LEAD_TICKS * 306;
      const close = Math.abs(dt) <= s.timingWindowTicks();
      g.ellipse(x, 148, 17, 17).fill(close ? 0x5d7e51 : 0x30291f);
      drawTrainingArrow(g, x, 148, prompt.cue, close ? 0xffe575 : 0xd8d1be, 0.75);
    }
    const streaks = Math.min(7, 2 + s.combo);
    for (let index = 0; index < streaks; index += 1) {
      g.rect(18 + index * 15, 191 + (index % 3) * 7, 9 + index, 2).fill({ color: 0xe9d8a3, alpha: 0.32 });
    }
    this.drawHorseShadow(HORSE_X, GROUND_Y);
    this.setHorse(HORSE_X, GROUND_Y, 'gallop', s.tick * (0.22 + Math.min(0.12, s.combo * 0.01)));
  }

  private drawDesensitize(): void {
    const s = this.session as DesensitizeSession;
    const g = this.world;
    const prompt = s.nextPrompt();
    if (prompt) {
      const dt = prompt.tick - s.tick;
      if (dt <= PROMPT_LEAD_TICKS && dt >= -s.timingWindowTicks()) {
        const index = s.nextPromptIndex();
        const side = prompt.cue === 'left' ? -1 : prompt.cue === 'right' ? 1 : index % 2 === 0 ? -1 : 1;
        const x = side < 0 ? 78 : 402;
        const close = Math.abs(dt) <= s.timingWindowTicks();
        const jitter = close && prompt.cue !== 'decoy' ? Math.round(Math.sin(this.lastNowMs / 36) * 4) : 0;
        drawSpookObject(g, prompt.object, x + jitter, 218, side < 0, prompt.cue === 'decoy' ? 0.42 : 1);
        if (close) {
          for (let line = 0; line < 3; line += 1) {
            const lx = x + side * (25 + line * 7);
            g.rect(lx, 158 + line * 9, 3, 9).fill(prompt.cue === 'decoy' ? 0x8b8374 : 0xd9a441);
          }
        }
      }
    }
    const sway = prompt && prompt.cue !== 'decoy' && Math.abs(prompt.tick - s.tick) <= s.timingWindowTicks()
      ? (prompt.cue === 'left' ? -1 : 1) * 7
      : 0;
    this.drawHorseShadow(240 + sway, GROUND_Y);
    this.setHorse(240 + sway, GROUND_Y, 'idle', this.lastNowMs / 600, s.calm < 0.3 ? 0xffc3ad : 0xffffff);
  }

  private drawTrust(): void {
    const s = this.session as TrustSession;
    const g = this.world;
    const duration = s.phaseDurationTicks();
    const phaseP = clamp01(s.phaseTick / Math.max(1, duration));
    let obstacleX = 190;
    let horseX = 126;
    let gait: 'idle' | 'gallop' | 'tuck' = 'idle';
    if (s.phase === 'approach') {
      obstacleX = 420 - phaseP * 230;
      gait = 'gallop';
    } else if (s.phase === 'hesitate') {
      obstacleX = 190;
      horseX = 132;
    } else if (s.phase === 'calm') {
      obstacleX = 190 - phaseP * 70;
      horseX = 132 + phaseP * 80;
      gait = 'gallop';
    } else if (s.phase === 'gap') {
      obstacleX = 120 - phaseP * 100;
      horseX = 215 + phaseP * 30;
      gait = 'gallop';
    }
    g
      .poly([0, 240, 100, 215, 260, 211, 480, 236, 480, 270, 0, 270]).fill(0x80613f)
      .rect(70, 211, 360, 3).fill({ color: 0xd7bc7b, alpha: 0.4 });
    if (s.phase !== 'over') {
      drawTrustObstacle(g, TRUST_OBSTACLES[s.obstacle % TRUST_OBSTACLES.length]!, obstacleX, 218);
    }
    // A loose lead rope makes the stop/listen/recommit sequence visible.
    g.poly([horseX - 5, 185, horseX - 20, 199, horseX - 2, 207]).fill({ color: 0xd9c9a2, alpha: 0.55 });
    this.drawHorseShadow(horseX, GROUND_Y);
    this.setHorse(horseX, GROUND_Y, gait, this.travel * 0.22, s.calm < 0.3 ? 0xffc3ad : 0xffffff);
  }

  private drawHorseShadow(x: number, groundY: number): void {
    this.world.ellipse(x, groundY + 1, 25, 5).fill({ color: 0x211810, alpha: 0.35 });
  }

  private setHorse(
    x: number,
    groundY: number,
    gait: 'idle' | 'gallop' | 'tuck',
    frame: number,
    tint = 0xffffff,
  ): void {
    let anchor = HORSE_GALLOP.anchor;
    let height = HORSE_GALLOP.h;
    if (gait === 'idle') {
      this.horse.texture = this.idleFrames[Math.floor(Math.abs(frame)) % this.idleFrames.length]!;
      anchor = HORSE_IDLE.anchor;
      height = HORSE_IDLE.h;
    } else if (gait === 'tuck') {
      this.horse.texture = this.tuckTexture;
      anchor = HORSE_TUCK.anchor;
      height = HORSE_TUCK.h;
    } else {
      this.horse.texture = this.gallopFrames[Math.floor(Math.abs(frame)) % this.gallopFrames.length]!;
    }
    this.horse.visible = true;
    this.horse.tint = tint;
    this.horse.rotation = 0;
    this.horse.scale.set(1);
    this.horse.position.set(Math.round(x) - anchor[0], Math.round(groundY) - height);
  }

  private readState(): HudState {
    switch (this.mode) {
      case 'furlongs': {
        const s = this.session as FurlongsSession;
        const work = s.segmentNow().work;
        return {
          cue: work ? 'PUSH THE GREEN ↑' : 'RECOVER — RELEASE',
          status: `pace ${Math.round(s.pace * 100)} · heat ${Math.round(s.heat * 100)}`,
          clock: `⏱ ${((s.durationTicks - s.tick) / 30).toFixed(1)}s`,
          meter: s.heat,
          meterLabel: 'HEAT',
          cueTone: work ? 'good' : 'go',
          meterTone: s.heat > 0.82 ? 'danger' : s.heat > 0.65 ? 'warning' : 'safe',
        };
      }
      case 'poles': {
        const s = this.session as PolesSession;
        const target = s.nextTarget();
        return {
          cue: target ? (target.side < 0 ? 'GET LEFT ←' : 'GET RIGHT →') : 'RUN THROUGH',
          status: `combo ×${s.combo} · ${s.misses} missed`,
          clock: `⏱ ${((s.durationTicks - s.tick) / 30).toFixed(1)}s`,
          meter: (s.p + 1) / 2,
          meterLabel: 'TRACK POSITION',
          cueTone: 'go',
          meterTone: 'position',
        };
      }
      case 'hills': {
        const s = this.session as HillsSession;
        const climb = s.segmentNow().climb;
        return {
          cue: climb ? 'DRIVE UPHILL ↑' : 'DOWNHILL — RELEASE',
          status: `effort ${Math.round(s.effort * 100)}%`,
          clock: `⏱ ${((s.durationTicks - s.tick) / 30).toFixed(1)}s`,
          meter: s.effort,
          meterLabel: 'EFFORT',
          cueTone: climb ? 'good' : 'go',
          meterTone: s.effort < 0.18 ? 'danger' : s.effort < 0.35 ? 'warning' : 'safe',
        };
      }
      case 'tempo': {
        const s = this.session as TempoSession;
        const beat = s.nextBeat();
        const dt = beat ? beat.tick - s.tick : 999;
        const close = Math.abs(dt) <= s.timingWindowTicks();
        return {
          cue: close ? 'TAP ↑' : dt <= 18 ? 'READY…' : 'WATCH THE HOOFPRINTS',
          status: `rhythm ×${s.combo} · ${s.misses} missed`,
          clock: `⏱ ${((s.durationTicks - s.tick) / 30).toFixed(1)}s`,
          meter: s.breath,
          meterLabel: 'BREATH',
          cueTone: close ? 'good' : 'quiet',
          meterTone: s.breath < 0.25 ? 'danger' : s.breath < 0.5 ? 'warning' : 'safe',
        };
      }
      case 'flyingStart': {
        const s = this.session as FlyingStartSession;
        const cue = s.phase === 'charge' ? 'HOLD TO LOAD ↑'
          : s.phase === 'release' ? 'GO — RELEASE!'
            : s.phase === 'gap' ? 'RESETTING…' : 'RUN THROUGH';
        return {
          cue,
          status: `start ${Math.min(s.round + 1, s.rounds)}/${s.rounds} · ${s.faults} early`,
          clock: `round ${Math.min(s.round + 1, s.rounds)}/${s.rounds}`,
          meter: s.phase === 'charge' ? s.chargeFraction() : s.lastGrade,
          meterLabel: s.phase === 'charge' ? 'LOAD' : 'LAST BREAK',
          cueTone: s.phase === 'release' ? 'good' : s.phase === 'gap' ? 'quiet' : 'go',
          meterTone: s.faults >= 2 ? 'danger' : 'safe',
        };
      }
      case 'gears': {
        const s = this.session as GearsSession;
        const prompt = s.nextPrompt();
        const dt = prompt ? prompt.tick - s.tick : 999;
        const close = Math.abs(dt) <= s.timingWindowTicks();
        const label = prompt ? arrowLabel(prompt.cue) : '✓';
        return {
          cue: close ? `${label} NOW` : dt <= PROMPT_LEAD_TICKS ? `NEXT ${label}` : 'EYES UP',
          status: `combo ×${s.combo} · ${s.errors} errors`,
          clock: `⏱ ${((s.durationTicks - s.tick) / 30).toFixed(1)}s`,
          meter: s.prompts.length ? s.hits / s.prompts.length : 0,
          meterLabel: 'CHANGES HIT',
          cueTone: close ? 'good' : 'quiet',
          meterTone: s.errors > 4 ? 'warning' : 'safe',
        };
      }
      case 'desensitize': {
        const s = this.session as DesensitizeSession;
        const prompt = s.nextPrompt();
        const dt = prompt ? prompt.tick - s.tick : 999;
        const close = Math.abs(dt) <= s.timingWindowTicks();
        let cue = 'SCAN THE PEN';
        if (prompt && dt <= PROMPT_LEAD_TICKS) {
          const object = OBJECT_LABEL[prompt.object];
          if (prompt.cue === 'decoy') cue = close ? `${object} NOISE — STAY STILL` : `WATCH: DIM ${object}`;
          else {
            const side = prompt.cue === 'left' ? 'LEFT' : 'RIGHT';
            const counter = prompt.cue === 'left' ? '→' : '←';
            cue = close ? `${object} ${side} — COUNTER ${counter}` : `WATCH: ${object} ${side}`;
          }
        }
        return {
          cue,
          status: `calm ${Math.round(s.calm * 100)}% · ${s.mistakes} mistakes`,
          clock: `⏱ ${((s.durationTicks - s.tick) / 30).toFixed(1)}s`,
          meter: s.calm,
          meterLabel: 'CALM',
          cueTone: prompt?.cue === 'decoy' ? 'quiet' : close ? 'go' : 'quiet',
          meterTone: s.calm < 0.3 ? 'danger' : s.calm < 0.55 ? 'warning' : 'safe',
        };
      }
      case 'trust': {
        const s = this.session as TrustSession;
        const labels = {
          approach: 'APPROACH — HOLD ↑',
          hesitate: 'SHE HESITATES — RELEASE',
          calm: 'RECOMMIT — HOLD ↑',
          gap: 'GOOD — WALK ON',
          over: 'COURSE CLEAR',
        } as const;
        return {
          cue: labels[s.phase],
          status: `obstacle ${Math.min(s.obstacle + 1, s.obstacles)}/${s.obstacles} · calm ${Math.round(s.calm * 100)}%`,
          clock: `${s.clean}/${s.obstacles} clean`,
          meter: s.calm,
          meterLabel: 'TRUST',
          cueTone: s.phase === 'hesitate' ? 'go' : s.phase === 'gap' ? 'good' : 'quiet',
          meterTone: s.calm < 0.3 ? 'danger' : s.calm < 0.55 ? 'warning' : 'safe',
        };
      }
    }
  }

  private captureFeedback(): void {
    switch (this.mode) {
      case 'furlongs': {
        const s = this.session as FurlongsSession;
        const phase = s.segmentNow().work ? 'work' : 'recover';
        if (this.lastPhase && phase !== this.lastPhase) this.showFlash(phase === 'work' ? 'WORK!' : 'RECOVER!', phase === 'work' ? 'good' : 'go');
        this.lastPhase = phase;
        break;
      }
      case 'poles': {
        const s = this.session as PolesSession;
        const resolved = s.poles.filter((pole) => pole.cleared !== null).length;
        if (resolved > this.lastA) {
          const cleared = s.poles[resolved - 1]?.cleared ?? false;
          this.showFlash(cleared ? `CLEAR ×${s.combo}` : 'WRONG SIDE', cleared ? 'good' : 'bad');
        }
        this.lastA = resolved;
        break;
      }
      case 'hills': {
        const phase = (this.session as HillsSession).segmentNow().climb ? 'climb' : 'coast';
        if (this.lastPhase && phase !== this.lastPhase) this.showFlash(phase === 'climb' ? 'CLIMB!' : 'COAST!', phase === 'climb' ? 'good' : 'go');
        this.lastPhase = phase;
        break;
      }
      case 'tempo': {
        const s = this.session as TempoSession;
        if (s.hits > this.lastA) this.showFlash(`ON BEAT ×${s.combo}`, 'good');
        else if (s.misses > this.lastB) this.showFlash('MISSED BEAT', 'bad');
        this.lastA = s.hits;
        this.lastB = s.misses;
        break;
      }
      case 'flyingStart': {
        const s = this.session as FlyingStartSession;
        if (s.round > this.lastA) {
          this.burstVisualTicks = 18;
          const label = s.lastGrade >= 0.9 ? 'SHARP BREAK!' : s.lastGrade >= 0.6 ? 'GOOD BREAK' : s.lastGrade > 0 ? 'LATE RELEASE' : 'NO LAUNCH';
          this.showFlash(label, s.lastGrade >= 0.6 ? 'good' : 'bad');
        }
        if (s.faults > this.lastB) this.showFlash('EARLY RELEASE!', 'bad');
        this.lastA = s.round;
        this.lastB = s.faults;
        break;
      }
      case 'gears': {
        const s = this.session as GearsSession;
        if (s.hits > this.lastA) this.showFlash(`CLEAN CHANGE ×${s.combo}`, 'good');
        else if (s.errors > this.lastB) this.showFlash('WRONG GEAR', 'bad');
        this.lastA = s.hits;
        this.lastB = s.errors;
        break;
      }
      case 'desensitize': {
        const s = this.session as DesensitizeSession;
        if (s.correct > this.lastA) this.showFlash('CALM READ', 'good');
        else if (s.mistakes > this.lastB) this.showFlash('STARTLED!', 'bad');
        this.lastA = s.correct;
        this.lastB = s.mistakes;
        break;
      }
      case 'trust': {
        const s = this.session as TrustSession;
        if (s.obstacle > this.lastA) this.showFlash(s.clean > this.lastB ? 'TRUSTED CROSSING!' : 'RUSHED IT', s.clean > this.lastB ? 'good' : 'bad');
        this.lastA = s.obstacle;
        this.lastB = s.clean;
        break;
      }
    }
  }

  private showFlash(text: string, tone: 'go' | 'good' | 'bad'): void {
    this.flash.textContent = text;
    this.flash.className = 'race-flash training-flash';
    void this.flash.offsetWidth;
    this.flash.className = `race-flash training-flash show ${tone}`;
  }

  private endEarly(): void {
    if (!this.session.finished) this.runner.fastForward(this.session);
  }

  private showResults(): void {
    this.resultsShown = true;
    const score = this.session.score();
    const finish = (): void => {
      const tape = [...this.runner.tape];
      this.ctx.scenes.pop();
      this.options.onDone(tape);
    };
    this.disposeResults = showTrainingResults(
      this.ctx.uiRoot,
      {
        title: `${MINIGAMES[this.mode].name} complete`,
        detail: score.detail,
        xp: xpFor(score.raw, this.launch.xpMult),
        failed: score.failed,
      },
      finish,
    );
    this.unbind?.();
    this.unbind = bindKeys([
      { code: 'Enter', inModal: true, handler: finish },
      { code: 'Escape', inModal: true, handler: finish },
    ]);
  }

  exit(): void {
    this.unbind?.();
    this.collector.dispose();
    this.disposeResults?.();
    this.hud.remove();
    this.container.destroy({ children: true });
  }
}
